import { Link } from "react-router-dom";
import PageLayout from "@/pages/home/components/PageLayout";
import { useScrollFadeIn } from "@/hooks/useScrollFadeIn";

export default function SessionIntroPage() {
  const { ref: heroRef, style: heroStyle } = useScrollFadeIn<HTMLDivElement>();
  const { ref: conceptRef, style: conceptStyle } = useScrollFadeIn<HTMLDivElement>({ delay: 0.1 });
  const { ref: featuresRef, style: featuresStyle } = useScrollFadeIn<HTMLDivElement>({ delay: 0.15 });
  const { ref: ctaRef, style: ctaStyle } = useScrollFadeIn<HTMLDivElement>({ delay: 0.1 });

  return (
    <PageLayout>
      <div className="bg-white min-h-screen">
        {/* Hero 導入文 */}
        <section className="relative flex items-center justify-center py-24 md:py-32 px-4 md:px-10">
          <div className="max-w-3xl mx-auto text-center" ref={heroRef} style={heroStyle}>
            <p className="text-[11px] md:text-xs tracking-[0.3em] text-amber-600 uppercase mb-6 md:mb-8">
              Private Session
            </p>
            <h1 className="text-2xl md:text-4xl font-normal text-stone-800 leading-[1.8] md:leading-[1.9] tracking-wide">
              SM・M性感に特化した
              <br className="md:hidden" />
              個人セッション
            </h1>
            <div className="w-12 h-0.5 bg-amber-400 mx-auto mt-6 mb-8 md:mt-7 md:mb-10"></div>
            <p className="text-[15px] md:text-lg text-stone-600 leading-[2.2] md:leading-[2.4] tracking-wide">
              感覚を大切にしながら、
              <br className="md:hidden" />
              ひとりひとりの性癖や快楽、欲求に寄り添った
              <br />
              完全予約制セッションです
            </p>
          </div>
        </section>

        {/* コンセプト */}
        <section className="py-16 md:py-20 px-4 md:px-10 bg-stone-50" ref={conceptRef} style={conceptStyle}>
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="font-en text-2xl md:text-3xl font-normal text-stone-800 tracking-widest">
                Concept
              </h2>
              <div className="w-10 h-0.5 bg-amber-400 mx-auto mt-3 mb-4 md:mt-4 md:mb-5"></div>
            </div>

            <div className="space-y-8 md:space-y-10">
              <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-white border border-stone-200 rounded-lg">
                  <i className="ri-heart-3-line text-amber-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="text-sm md:text-base font-medium text-stone-800 tracking-wide mb-2">
                    感覚を大切にする
                  </h3>
                  <p className="text-sm text-stone-500 leading-[1.9] tracking-wide">
                    身体の反応や心の動きに耳を澄ませ、無理のないペースで深みへと誘います。焦らず、急がず、あなた自身の感覚と向き合う時間を大切にしています。
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-white border border-stone-200 rounded-lg">
                  <i className="ri-user-heart-line text-amber-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="text-sm md:text-base font-medium text-stone-800 tracking-wide mb-2">
                    ひとりひとりに寄り添う
                  </h3>
                  <p className="text-sm text-stone-500 leading-[1.9] tracking-wide">
                    同じプレイは二つとしてありません。あなたの性癖、フェチ、好奇心、そして今の気持ちに合わせて、二人だけの特別な時間を丁寧に紡ぎ出します。
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-white border border-stone-200 rounded-lg">
                  <i className="ri-lock-line text-amber-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="text-sm md:text-base font-medium text-stone-800 tracking-wide mb-2">
                    完全予約制・完全プライベート
                  </h3>
                  <p className="text-sm text-stone-500 leading-[1.9] tracking-wide">
                    他の誰とも被らない完全予約制。プライバシーを徹底的に守り、あなたが安心して自分を解放できる空間をご用意します。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 対象者 */}
        <section className="py-16 md:py-20 px-4 md:px-10" ref={featuresRef} style={featuresStyle}>
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="font-en text-2xl md:text-3xl font-normal text-stone-800 tracking-widest">
                For You
              </h2>
              <div className="w-10 h-0.5 bg-amber-400 mx-auto mt-3 mb-4 md:mt-4 md:mb-5"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              <div className="bg-stone-50 p-5 md:p-6 border border-stone-100 text-center">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-seedling-line text-amber-600 text-xl"></i>
                </div>
                <h3 className="text-sm font-medium text-stone-800 tracking-wide mb-2">
                  初めての方
                </h3>
                <p className="text-xs text-stone-500 leading-[1.8] tracking-wide">
                  SM未経験でも大丈夫。不安や緊張を和らげながら、はじめての一歩を優しくサポートします。
                </p>
              </div>

              <div className="bg-stone-50 p-5 md:p-6 border border-stone-100 text-center">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-compass-line text-amber-600 text-xl"></i>
                </div>
                <h3 className="text-sm font-medium text-stone-800 tracking-wide mb-2">
                  探求したい方
                </h3>
                <p className="text-xs text-stone-500 leading-[1.8] tracking-wide">
                  自分の性癖や快楽の深みを知りたい方。新しい感覚や可能性を一緒に探っていきましょう。
                </p>
              </div>

              <div className="bg-stone-50 p-5 md:p-6 border border-stone-100 text-center">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-refresh-line text-amber-600 text-xl"></i>
                </div>
                <h3 className="text-sm font-medium text-stone-800 tracking-wide mb-2">
                  リピートの方
                </h3>
                <p className="text-xs text-stone-500 leading-[1.8] tracking-wide">
                  前回をより深く、あるいは新しい方向へ。回数を重ねるごとに育まれる信頼関係の中で、より豊かな時間を。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 px-4 md:px-10 bg-stone-50" ref={ctaRef} style={ctaStyle}>
          <div className="max-w-xl mx-auto text-center">
            <p className="text-[11px] md:text-xs tracking-[0.3em] text-amber-600 uppercase mb-4 md:mb-5">
              Next Step
            </p>
            <h2 className="text-xl md:text-2xl font-normal text-stone-800 tracking-wide mb-4 md:mb-5">
              セッションメニューを確認する
            </h2>
            <p className="text-sm text-stone-500 leading-[1.9] tracking-wide mb-8 md:mb-10">
              具体的なプレイ内容や対応可能なメニュー、NG行為などをご案内しています。
              <br />
              まずは目を通して、ご不明な点があればお気軽にお問い合わせください。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
              <Link
                to="/session"
                className="flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-3.5 bg-stone-800 text-white text-sm tracking-[0.2em] hover:bg-amber-700 transition-colors duration-300 cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-file-list-line text-base"></i>
                </div>
                セッションメニュー
              </Link>
              <Link
                to="/reservation"
                className="flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-3.5 border border-stone-300 text-stone-700 text-sm tracking-[0.2em] hover:bg-stone-100 transition-colors duration-300 cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-check-line text-base"></i>
                </div>
                ご予約はこちら
              </Link>
            </div>
          </div>
        </section>
      </div>
    </PageLayout>
  );
}